import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import SearchBar from '../components/SearchBar'
import Newsletter from '../components/Newsletter'
import CodeCard from '../components/CodeCard'
import Faq from '../components/Faq'
import SchemaMarkup from '../components/SchemaMarkup'
import supabase from '../services/supabase'
import SponsorBanner from '../components/SponsorBanner'
import '../styles/global.css'

const DirectoryCnaePage = () => {
  const [cnaeCodes, setCnaeCodes] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [limit, setLimit] = useState(20)
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(true)
  const navigate = useNavigate()

  const faqData = [
    {
      question: '¿Qué es el CNAE 2025?',
      answer:
        'Los códigos CNAE 2025 representan la actualización de la Clasificación Nacional de Actividades Económicas que entró en vigor el 16 de enero de 2025, con un periodo de adaptación hasta el 30 de junio de 2025 para que las empresas actualicen sus códigos.\n\nEsta actualización responde a la necesidad de adaptar la clasificación a los cambios estructurales en la economía y los avances tecnológicos recientes.',
    },
    {
      question: '¿Cuándo entrará en vigor el CNAE 2025?',
      answer:
        'Entrada en vigor:\nEl Real Decreto 10/2025, que aprueba la CNAE 2025, entró en vigor el 16 de enero de 2025.\n\nPeriodo de adaptación:\nLas empresas registradas antes del 16 de enero de 2025 tienen hasta el 30 de junio de 2025 para comunicar su nueva codificación CNAE 2025.\n\nNuevas incorporaciones:\nLas empresas que se den de alta a partir del 16 de enero de 2025 deberán comunicar su codificación CNAE 2025 en el momento de su alta.',
    },
    {
      question:
        '¿Cuáles son los principales cambios entre la CNAE 2025 y la CNAE 2009?',
      answer:
        'La CNAE 2025 introduce cambios estructurales significativos respecto a la versión 2009:\n\nCambios cuantitativos: Aumenta de 629 a 664 clases, de 272 a 287 grupos, y pasa de 21 a 22 secciones.\n\nModificaciones clave:\n\nNuevas categorías para actividades de intermediación digital\nReestructuración del sector manufacturero\nIntegración de la antigua división 45 (venta y reparación de vehículos) en otras divisiones\nEliminación del criterio de canal de venta en comercio minorista\nNueva clasificación energética basada en fuentes renovables\nDivisión de la sección J en dos secciones independientes',
    },
    {
      question:
        '¿Qué es la Tabla de correspondencia entre la CNAE 2025 y la CNAE 2009?',
      answer:
        'Las tablas de correspondencia CNAE permiten comparar datos estadísticos entre diferentes versiones de la clasificación. Estas pueden ser:\n\nCorrespondencias 1 a 1: Una clase en CNAE-2009 corresponde exactamente a una clase en CNAE-2025\nCorrespondencias n a 1: Varias clases de una versión se unifican en una sola clase en la otra\nCorrespondencias 1 a m: Una clase se divide en múltiples clases en la otra versión\nCorrespondencias n a m: Varias clases de una versión corresponden a múltiples clases en la otra',
    },
    {
      question:
        '¿Cómo encuentro el código CNAE 2025 correspondiente a mi actividad económica?',
      answer:
        'Para identificar tu código CNAE 2025:\n\nUtiliza nuestro buscador introduciendo palabras clave relacionadas con tu actividad\nNavega por las secciones y divisiones del directorio\nConsulta las descripciones detalladas de cada código\nVerifica que el código contempla todas las actividades que realizas\n\nEs importante seleccionar el código más específico posible que describa tu actividad principal.',
    },
    {
      question:
        '¿Cómo afecta la implementación de la CNAE 2025 a las empresas ya registradas?',
      answer:
        '​La implementación de la Clasificación Nacional de Actividades Económicas 2025 (CNAE-2025) implica que las empresas registradas antes del 16 de enero de 2025 deben actualizar su código de actividad económica en los registros administrativos pertinentes antes del 30 de junio de 2025.\n\nEsta actualización es esencial para garantizar la coherencia y comparabilidad de las estadísticas económicas y facilitar la toma de decisiones informadas por parte de diferentes actores.\n\nDurante el periodo de transición, que abarca desde el 16 de enero hasta la entrada en vigor de la legislación de la Seguridad Social adaptada a la CNAE-2025, las empresas deberán comunicar simultáneamente su código de actividad económica según la CNAE-2009 y la CNAE-2025. Esta doble codificación permitirá una adaptación progresiva al nuevo sistema.\n\nEs importante destacar que la actualización del código CNAE puede influir en aspectos laborales y fiscales de la empresa, ya que determina el convenio colectivo aplicable y las tarifas de cotización a la Seguridad Social. Por ello, se recomienda revisar las actividades realizadas y, en caso necesario, consultar con asesores o gestores expertos para asegurar una correcta adaptación al nuevo sistema de clasificación.',
    },
    {
      question:
        '¿Qué plazo tengo para actualizar mi código CNAE al nuevo estándar?',
      answer:
        'El plazo para actualizar el código CNAE de tu empresa al nuevo estándar CNAE 2025 es hasta el 30 de junio de 2025.\n\nLas empresas registradas antes del 16 de enero de 2025 deben realizar esta actualización antes de esa fecha. Las empresas registradas después de esta fecha deberán hacerlo al momento de su incorporación.\n\nEs importante realizar la actualización dentro de este plazo para cumplir con los requisitos legales y fiscales establecidos.',
    },
    {
      question:
        '¿Qué sucede si no actualizo mi código CNAE según la nueva clasificación?',
      answer:
        'Si no actualizas tu código CNAE según la nueva clasificación CNAE 2025, podrías enfrentar problemas con el cumplimiento de obligaciones fiscales y legales, ya que el código CNAE es utilizado para determinar las tasas impositivas y las cotizaciones a la Seguridad Social, entre otros aspectos.\n\nAdemás, la falta de actualización puede afectar la coherencia de los datos estadísticos y la correcta asignación de actividades económicas en registros oficiales.',
    },
    {
      question:
        '¿Dónde puedo consultar la lista completa de códigos y descripciones de la CNAE 2025?',
      answer:
        'Puedes consultar la lista completa de códigos y descripciones de la CNAE 2025 en el sitio web oficial del Instituto Nacional de Estadística (INE).\n\nSin embargo, para una forma más ágil y sencilla de encontrar el código exacto que corresponde a tu actividad económica, te recomendamos utilizar nuestro Conversor IAE-CNAE. Con esta herramienta, podrás buscar y convertir rápidamente entre los códigos IAE y CNAE, obteniendo resultados en tiempo real y con acceso a toda la información relevante de cada código.',
    },
    {
      question: '¿Cómo puedo convertir un código CNAE 2025 a IAE?',
      answer:
        'Para convertir un código IAE a CNAE, simplemente utiliza nuestra herramienta de conversión.\n\nIngresa el código IAE en el campo de búsqueda y selecciona el resultado correcto de la lista de sugerencias. Nuestro conversor te proporcionará el código CNAE correspondiente a tu actividad, asegurando que puedas cumplir con los requisitos fiscales y administrativos de manera eficiente.',
    },
    {
      question:
        '¿Cómo actualizo mi código CNAE actual al nuevo CNAE 2025 correspondiente?',
      answer:
        'La actualización de tu código CNAE al nuevo estándar CNAE 2025 es un proceso necesario para todas las empresas. Debes realizar esta actualización antes del 30 de junio de 2025.\n\nPuedes utilizar las tablas de correspondencia entre la CNAE 2025 y la CNAE 2009 proporcionadas por el Instituto Nacional de Estadística (INE) o, simplemente, utilizar nuestro conversor.',
    },
  ]

  const fetchCnaeCodes = async (
    limitParam = limit,
    offsetParam = offset,
    append = false
  ) => {
    try {
      setIsLoading(true)
      const { data, error } = await supabase.rpc(
        'get_most_searched_cnae_2025_codes',
        {
          limit_param: limitParam,
          offset_param: offsetParam,
        }
      )

      if (error) throw error

      if (!data || data.length === 0) {
        console.warn('No CNAE 2025 codes found')
        setHasMore(false)
        if (!append) {
          setCnaeCodes([])
        }
        return
      }

      // If we're appending, add to existing codes, otherwise replace
      if (append) {
        setCnaeCodes((prevCodes) => [...prevCodes, ...data])
      } else {
        setCnaeCodes(data)
      }

      // Check if we have more data to load
      setHasMore(data.length === limitParam)
    } catch (error) {
      console.error('Error fetching CNAE codes:', error)
      setError('Error al cargar los códigos CNAE')
    } finally {
      setIsLoading(false)
    }
  }

  // Load more codes
  const handleLoadMore = () => {
    const newOffset = offset + limit
    setOffset(newOffset)
    fetchCnaeCodes(limit, newOffset, true)
  }

  useEffect(() => {
    fetchCnaeCodes()
  }, [])

  return (
    <div className='container mx-auto px-4'>
      {/* Header Section */}
      <header className='py-8 md:py-16 text-center'>
        <h1 className='text-2xl md:text-4xl font-bold mb-4 text-text-primary'>
          Listado de Códigos CNAE 2025 Actualizado
        </h1>
        <p className='text-text-secondary text-lg mb-8 max-w-2xl mx-auto'>
          Explora la base de datos completa de códigos CNAE 2025 (Clasificación
          Nacional de Actividades Económicas). Encuentra, filtra y consulta en
          tiempo real los códigos oficiales con sus descripciones detalladas y
          equivalencias IAE.
        </p>

        {/* Search Bar */}
        <div className='max-w-xl mx-auto mb-6'>
          <SearchBar
            type='cnae'
            hideConvertButton={true}
            onCodeSelect={(code) => {
              // Redirect to details page when a code is selected
              navigate(`/cnae-2025/${code.code.replace('.', '')}/${code.slug}`)
            }}
          />
        </div>

        {/* Sponsor Banner */}
        <SponsorBanner />
      </header>

      {/* CNAE Codes Section */}
      <section className='py-8 md:py-12'>
        <h2 className='text-xl md:text-3xl font-bold mb-6 text-text-primary text-center'>
          Códigos CNAE 2025 Más Populares
        </h2>

        {/* SearchResultsPage Schema */}
        {cnaeCodes.length > 0 && (
          <SchemaMarkup
            type='SearchResultsPage'
            data={{
              results: cnaeCodes.map((code) => ({
                title: `${code.cod_cnae} - ${code.titulo_cnae}`,
                url: `https://conversoriaecnae.es/cnae-2025/${code.cod_cnae.replace(
                  '.',
                  ''
                )}/${code.slug_cnae}`,
              })),
            }}
          />
        )}

        {/* ItemList Schema */}
        {cnaeCodes.length > 0 && (
          <SchemaMarkup
            type='ItemList'
            data={{
              name: 'Códigos CNAE 2025 Más Populares',
              description:
                'Lista de los códigos CNAE 2025 más buscados en España',
              items: cnaeCodes.map((code) => ({
                title: `${code.cod_cnae} - ${code.titulo_cnae}`,
                code: code.cod_cnae,
                url: `https://conversoriaecnae.es/cnae-2025/${code.cod_cnae.replace(
                  '.',
                  ''
                )}/${code.slug_cnae}`,
              })),
            }}
          />
        )}

        {isLoading && cnaeCodes.length === 0 ? (
          <div className='flex justify-center'>
            <div className='w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin'></div>
          </div>
        ) : error ? (
          <div className='text-center text-red-500'>{error}</div>
        ) : (
          <>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
              {cnaeCodes.map((code, index) => (
                <CodeCard
                  key={index}
                  code={code.cod_cnae}
                  title={code.titulo_cnae}
                  type='cnae'
                  slug={code.slug_cnae}
                  searchCount={code.cant_busquedas_cnae}
                />
              ))}
            </div>

            {/* Load More Button */}
            {hasMore && cnaeCodes.length > 0 && (
              <div className='flex justify-center mt-8'>
                <button
                  onClick={handleLoadMore}
                  className='px-6 py-2 bg-surface text-white rounded-md hover:bg-primary transition-colors duration-300 flex items-center'
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className='mr-2'>Cargando...</span>
                      <div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin'></div>
                    </>
                  ) : (
                    'Cargar Más Códigos'
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {/* Newsletter Section */}
      <Newsletter />

      {/* FAQ Section */}
      <Faq
        title='Preguntas Frecuentes sobre Códigos CNAE 2025 (FAQ)'
        description='Resolvemos tus dudas sobre los códigos CNAE 2025, su aplicación y cómo utilizarlos correctamente para tu actividad económica. Información actualizada según las últimas disposiciones oficiales.'
        faqData={faqData}
      />
    </div>
  )
}

export default DirectoryCnaePage
